import React from 'react';
import { HeartOutlined } from '@ant-design/icons';
import './BondValuationCard.css';

const formatYieldPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${num.toFixed(2)}%`;
};

const formatSpreadBps = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const rounded = Math.round(num);
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded} bps`;
};

const formatTradeValueCr = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Cr`;
};

const getValuationBadge = (status) => {
  switch (status) {
    case 'UNDERVALUED':
      return { className: 'undervalued', label: 'Undervalued' };
    case 'OVERVALUED':
      return { className: 'overvalued', label: 'Overvalued' };
    case 'FAIR_VALUE':
    default:
      return { className: 'fair', label: 'Fair value' };
  }
};

const stripPpMld = (rating) => String(rating || '')
  .replace(/PP\s*-\s*MLD/gi, '')
  .replace(/PPMLD/gi, '')
  .replace(/\(\s*CE\s*\)/gi, '')
  .trim();

const normalizeRating = (rating) => stripPpMld(rating).toUpperCase().replace(/\s+/g, '');

const getShortAgencyName = (fullName) => {
  if (!fullName) return fullName;
  const upperName = fullName.toUpperCase();
  if (upperName.includes('CRISIL')) return 'CRISIL';
  if (upperName.includes('ICRA')) return 'ICRA';
  if (upperName.includes('CARE') || upperName.includes('CREDIT ANALYSIS')) return 'CARE';
  if (upperName.includes('BRICKWORK')) return 'BRICKWORK';
  if (upperName.includes('INDIA RATING') || upperName.includes('INDIA RATINGS')) return 'INDIA';
  if (upperName.includes('ACUITE')) return 'ACUITE';
  if (upperName.includes('INFOMERICS')) return 'INFOMERICS';
  return fullName;
};

const pickPrimaryRating = (creditRatings) => {
  if (!creditRatings || !Array.isArray(creditRatings) || creditRatings.length === 0) return null;
  const sorted = [...creditRatings].filter(Boolean);
  const aaa = sorted.find((r) => normalizeRating(r?.currentRating).includes('AAA'));
  if (aaa) return aaa;
  return sorted[0];
};

const truncate = (value, maxLen = 18) => {
  if (value == null) return value;
  const s = String(value);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const BondValuationCard = ({ valuation }) => {
  const badge = getValuationBadge(valuation.valuationStatus);
  const issuerName = valuation.issuerName?.trim() || '—';
  const primaryRating = pickPrimaryRating(valuation.creditRatings);
  const hasCreditRating = Boolean(primaryRating?.currentRating);
  const ratingDetailLine = primaryRating
    ? `${stripPpMld(primaryRating.currentRating)} by ${getShortAgencyName(primaryRating.creditRatingAgencyName)}`
    : '—';
  const ratingDetailLineShort = truncate(ratingDetailLine, 18);

  return (
    <article
      className={`sb-valuation-card sb-valuation-card--${badge.className}`}
      aria-label={`Bond valuation: ${valuation.issuerName || valuation.isin}`}
    >
      <div className="sb-valuation-card__head">
        <h4 className="sb-valuation-card__issuer">{issuerName}</h4>
        <div className="sb-valuation-card__meta-row">
          <div className="sb-valuation-card__meta">
            <span className="sb-valuation-card__isin">{valuation.isin || '—'}</span>
            <span className="sb-valuation-card__trade-value">
              Trade value {formatTradeValueCr(valuation.tradeValueCr)}
            </span>
          </div>
          <div className={`sb-valuation-card__badge sb-valuation-card__badge--${badge.className}`}>
            {badge.label}
          </div>
        </div>
      </div>

      <div className="sb-valuation-card__metrics">
        <div className="sb-valuation-card__yields">
          <div className="sb-valuation-card__yield-tile sb-valuation-card__yield-tile--primary">
            <span className="sb-valuation-card__tile-label">Fair yield</span>
            <span className="sb-valuation-card__tile-value sb-valuation-card__tile-value--highlight">
              {formatYieldPercent(valuation.yieldByScan)}
            </span>
          </div>
          <div className="sb-valuation-card__yield-tile">
            <span className="sb-valuation-card__tile-label">Market yield</span>
            <span className="sb-valuation-card__tile-value">{formatYieldPercent(valuation.ytmPercent)}</span>
          </div>
        </div>

        <div className="sb-valuation-card__footer">
          <div className="sb-valuation-card__footer-cell">
            <span className="sb-valuation-card__tile-label">Spread vs bench</span>
            <span className="sb-valuation-card__spread">{formatSpreadBps(valuation.spreadBps)}</span>
          </div>
          <div className="sb-valuation-card__footer-cell sb-valuation-card__footer-cell--rating">
            {hasCreditRating ? (
              <div className="sb-valuation-card__rating-pill">
                <div className="sb-valuation-card__shield" aria-hidden>
                  <HeartOutlined className="sb-valuation-card__shield-heart" />
                </div>
                <div className="sb-valuation-card__rating-copy">
                  <span className="sb-valuation-card__rating-label">Credit rating</span>
                  <span className="sb-valuation-card__rating-value" title={ratingDetailLine}>
                    {ratingDetailLineShort}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <span className="sb-valuation-card__tile-label">Credit rating</span>
                <span className="sb-valuation-card__spread">—</span>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default BondValuationCard;
